import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Link2 } from 'lucide-react';
import type { RelationTypeToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useRelationTypesList, useRelationTypeMutations } from '~/hooks/use-relation-types';
import { tokenToForm, type RelationTypeFormValues } from '~/lib/schemas/relation-type';
import { RelationTypeEditDialog } from './relation-type-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Relation types (Τύποι Σχέσεων) CRUD surface — parity with the legacy admin grid (Name/Code/Description/Visible/Sort). */
export function RelationTypesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useRelationTypesList();
	const { remove } = useRelationTypeMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<RelationTypeFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<RelationTypeToken | null>(null);

	const columns = useMemo<SystemGridColumn<RelationTypeToken>[]>(
		() => [
			{
				key: 'name',
				header: t('catalog.relationTypes.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'code',
				header: t('catalog.relationTypes.columns.code'),
				width: '180px',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'description',
				header: t('catalog.relationTypes.columns.description'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.desc ?? '',
				exportValue: o => o.desc ?? '',
				render: o => (
					<span className="truncate text-sm text-foreground-muted" title={o.desc ?? ''}>
						{o.desc || '—'}
					</span>
				)
			},
			{
				key: 'visible',
				header: t('catalog.relationTypes.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('catalog.relationTypes.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<RelationTypeToken>
				gridId="relation-types"
				title={t('catalog.relationTypes.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Link2 size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.relationTypes.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="relation-types"
			/>

			<RelationTypeEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.relationTypes.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.relationTypes.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
